import {Polygon} from './polygon';
import {Spot} from './spot';
import {Layer} from './layer';
import {GeoGroupData} from './geo-group-data';
import {ProjectComment} from '../../common/model/project-comment';

/**
 * What Clear Map should remove, as the dialog asks it.
 *
 * The kinds are the things a project really holds: areas and lines are both
 * polygons (a line has no surface), holes go with the area they are cut out
 * of, and map layers split into the images placed on the map and the imported
 * reference layers (MBTiles and big vector files).
 */
export interface ClearSelection {
  areas: boolean;
  lines: boolean;
  spots: boolean;
  /** The group definitions themselves. Shapes left behind become ungrouped. */
  groups: boolean;
  images: boolean;
  layers: boolean;
  /**
   * Comments are a conversation, often with the client, not part of the
   * drawing: offered on their own and never part of "Everything".
   */
  comments?: boolean;
  scope: ClearScope;
  /** For scope 'groups': whose contents go. NO_GROUP_ID stands for shapes in no group. */
  groupIds?: string[];
  /** For scope 'view': what the map shows. */
  bounds?: google.maps.LatLngBoundsLiteral;
}

/**
 * Where to clear: everything, what the map is showing now, or chosen groups.
 * Groups and layers have no place on the map, and layers belong to no group,
 * so the dialog only offers each kind where the scope means something for it.
 */
export type ClearScope = 'all' | 'view' | 'groups';

export const NO_GROUP_ID = '';

export interface ClearCounts {
  areas: number;
  lines: number;
  spots: number;
  groups: number;
  images: number;
  layers: number;
  comments: number;
}

export interface ClearPlan {
  /** The areas and lines chosen, plus the holes cut out of them. */
  polygons: Polygon[];
  spots: Spot[];
  groups: GeoGroupData[];
  layers: Layer[];
  comments: ProjectComment[];
  /** Shapes that stay while their group goes: they lose the group, not themselves. */
  ungrouped: Array<Polygon | Spot>;
  counts: ClearCounts;
  /** How many things the dialog says will go (holes count with their area). */
  total: number;
}

export function everything(): ClearSelection {
  return {areas: true, lines: true, spots: true, groups: true, images: true, layers: true, comments: false, scope: 'all'};
}

export function emptyCounts(): ClearCounts {
  return {areas: 0, lines: 0, spots: 0, groups: 0, images: 0, layers: 0, comments: 0};
}
